import React, { useState } from 'react';
// Native MUI Components
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  IconButton,
} from '@mui/material';
import { Close, Add, Remove } from '@mui/icons-material';
import { ApartmentType, PriceRule } from '@/types';
import { useI18n } from '@/hooks/useI18n';

interface AddApartmentTypeFormProps {
  onAddApartmentType: (scheme: Omit<ApartmentType, 'id'>) => void;
  trigger: React.ReactNode;
}

const AddApartmentTypeForm: React.FC<AddApartmentTypeFormProps> = ({
  onAddApartmentType,
  trigger
}) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const { t } = useI18n();
  const [priceRules, setPriceRules] = useState<PriceRule[]>([
    { occupancy: 1, pricePerPersonPerWeek: 0 }
  ]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name || priceRules.length === 0) {
      return;
    }

    onAddApartmentType({
      name,
      priceRules: priceRules.filter(rule => rule.occupancy > 0 && rule.pricePerPersonPerWeek > 0)
    });

    // Reset form
    setName('');
    setPriceRules([{ occupancy: 1, pricePerPersonPerWeek: 0 }]);
    setOpen(false);
  };

  const addPriceRule = () => {
    const nextOccupancy = priceRules.length > 0 ? Math.max(...priceRules.map(r => r.occupancy)) + 1 : 1;
    setPriceRules([...priceRules, { occupancy: nextOccupancy, pricePerPersonPerWeek: 0 }]);
  };

  const removePriceRule = (index: number) => {
    if (priceRules.length > 1) {
      setPriceRules(priceRules.filter((_, i) => i !== index));
    }
  };

  const updatePriceRule = (index: number, field: keyof PriceRule, value: number) => {
    setPriceRules(priceRules.map((rule, i) => 
      i === index ? { ...rule, [field]: value } : rule
    ));
  };

  return (
    <>
      <Box onClick={() => setOpen(true)} sx={{ cursor: 'pointer' }}>
        {trigger}
      </Box>
      
      <Dialog 
        open={open} 
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 1,
          }}
        >
          <Typography variant="h6" component="div">
            {t('apartmentType.form.addApartmentType')}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => setOpen(false)}
            sx={{
              color: 'grey.500',
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label={t('apartmentType.form.apartmentTypeName')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('apartmentType.form.apartmentTypeNamePlaceholder')}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                    {t('apartmentType.form.priceRules')}
                  </Typography>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={addPriceRule}
                    startIcon={<Add />}
                  >
                    {t('apartmentType.form.addRule')}
                  </Button>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {priceRules.map((rule, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <TextField
                        type="number"
                        inputProps={{ min: 1 }}
                        placeholder={t('apartmentType.form.occupancyPlaceholder')}
                        value={rule.occupancy}
                        onChange={(e) => updatePriceRule(index, 'occupancy', parseInt(e.target.value) || 0)}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        placeholder={t('apartmentType.form.pricePerPersonPerWeekPlaceholder')}
                        value={rule.pricePerPersonPerWeek}
                        onChange={(e) => updatePriceRule(index, 'pricePerPersonPerWeek', parseFloat(e.target.value) || 0)}
                        sx={{ flex: 1 }}
                      />
                      {priceRules.length > 1 && (
                        <IconButton
                          onClick={() => removePriceRule(index)}
                          size="small"
                          color="error"
                        >
                          <Remove />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setOpen(false)} 
            variant="outlined"
          >
            {t('apartmentType.form.cancel')}
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
          >
            {t('apartmentType.form.addApartmentType')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddApartmentTypeForm;
